import {
  FlexBox,
  Lightbox,
  Modal,
  ModalBody,
  ModalHead,
  Spinner,
  SrcType,
} from '@dixa/lib-shared-habla';
import { config } from 'lib-shared-backend-config';
import { Component, lazy, Suspense } from 'react';

import { ajaxSend } from '../../../../api/api';
import {
  isExcelAttachment,
  isImageAttachment,
  isPdfAttachment,
  isVideoAttachment,
} from '../../../../app/utils/attachments';

import VideoLightbox from './VideoLightbox';

const XlsxLightbox = lazy(
  () => import(/* webpackChunkName: "xlsxLightbox" */ './XlsxLightbox'),
);

const PDFLightbox = lazy(
  () => import(/* webpackChunkName: "pdflightbox" */ './PDFLightbox'),
);

type PropsT = {
  fileName: string | null;
  url: SrcType | string | null;
  closeModal: () => void;
  attachments: Array<{ fileName: string; url: string }>;
  currentIndex: number;
  goToNext: () => void;
  goToPrevious: () => void;
};

type StateT = {
  currentBlobUrl: string | null;
  isLoading: boolean;
};

export default class AttachmentsPreviewModal extends Component<PropsT, StateT> {
  constructor(props: PropsT) {
    super(props);
    this.state = {
      currentBlobUrl: null,
      isLoading: false,
    };
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown);
    this.fetchBlobUrl(this.props.url as string);
  }

  componentDidUpdate(prevProps: PropsT) {
    // If the URL changed (navigation), fetch the new blob URL
    if (prevProps.url !== this.props.url) {
      this.fetchBlobUrl(this.props.url as string);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
    // Clean up blob URL to prevent memory leaks (only for actual blob URLs)
    if (this.state.currentBlobUrl?.startsWith('blob:')) {
      window.URL.revokeObjectURL(this.state.currentBlobUrl);
    }
  }

  fetchBlobUrl = async (url: string | null) => {
    // If URL is null or empty, don't fetch
    if (!url) {
      this.setState({ currentBlobUrl: null, isLoading: false });
      return;
    }

    // If it's already a blob URL, use it directly
    if (url.startsWith('blob:')) {
      this.setState({ currentBlobUrl: url, isLoading: false });
      return;
    }

    // Check if this is an external URL that might have CORS restrictions
    const isExternalUrl = this.isExternalUrl(url);

    if (isExternalUrl) {
      // For external URLs (like Facebook/Instagram), use them directly
      // This avoids CORS issues with third-party CDNs
      this.setState({ currentBlobUrl: url, isLoading: false });
      return;
    }

    this.setState({ isLoading: true });

    try {
      const blob = await ajaxSend<Blob>({
        path: url,
        responseType: 'blob',
      });

      // Clean up previous blob URL (only for actual blob URLs)
      if (this.state.currentBlobUrl?.startsWith('blob:')) {
        window.URL.revokeObjectURL(this.state.currentBlobUrl);
      }

      const blobUrl = window.URL.createObjectURL(blob);
      this.setState({ currentBlobUrl: blobUrl, isLoading: false });
    } catch (error) {
      console.error('Failed to fetch attachment:', error);
      this.setState({ isLoading: false });
    }
  };

  /** Determines if a URL is external and might have CORS restrictions */
  isExternalUrl = (url: string): boolean => {
    try {
      const urlObj = new URL(url);
      const currentOrigin = window.location.origin;

      // Check if the URL is from a different origin
      if (urlObj.origin !== currentOrigin) {
        const hostname = urlObj.hostname;

        // Dixa domains that we control and can fetch as blobs
        const dixaDomains = [config.domain.service, config.domain.userContent];

        // If it's not a Dixa domain, treat it as external
        return !dixaDomains.some((domain) => hostname.includes(domain));
      }

      return false;
    } catch {
      // If URL parsing fails, assume it's not external
      return false;
    }
  };

  handleKeyDown = (event: KeyboardEvent) => {
    const { attachments, goToNext, goToPrevious, closeModal } = this.props;

    if (attachments.length <= 1) return;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        goToPrevious();
        break;
      case 'ArrowRight':
        event.preventDefault();
        goToNext();
        break;
      case 'Escape':
        event.preventDefault();
        closeModal();
        break;
    }
  };

  render() {
    const {
      fileName,
      url,
      closeModal,
      attachments,
      currentIndex,
      goToNext,
      goToPrevious,
    } = this.props;
    const { currentBlobUrl, isLoading } = this.state;
    const hasMultipleAttachments = attachments.length > 1;

    // Don't render anything if no URL (modal not supposed to be open)
    if (!url) {
      return null;
    }

    // Show loading state while fetching blob URL
    if (!currentBlobUrl) {
      return (
        <div style={{ position: 'relative' }}>
          <Lightbox
            isOpen={true}
            src=""
            onClose={closeModal}
            className="modal-z-middle"
            isLoading={true}
            showNavigation={hasMultipleAttachments}
            currentIndex={currentIndex}
            totalImages={attachments.length}
            onPrevious={goToPrevious}
            onNext={goToNext}
          />
        </div>
      );
    }

    if (fileName && isImageAttachment(fileName)) {
      return (
        <Lightbox
          isOpen={!!url}
          src={currentBlobUrl as SrcType}
          onClose={closeModal}
          className="modal-z-middle"
          showNavigation={hasMultipleAttachments}
          currentIndex={currentIndex}
          totalImages={attachments.length}
          onPrevious={goToPrevious}
          onNext={goToNext}
          isLoading={isLoading}
        />
      );
    }

    if (fileName && isExcelAttachment(fileName)) {
      return (
        <Suspense
          fallback={
            <Modal isOpen allowClickOutside onClose={closeModal}>
              <ModalHead title="" onClose={closeModal} />
              <ModalBody>
                <FlexBox width="540px" justifyContent="center">
                  <Spinner size={32} />
                </FlexBox>
              </ModalBody>
            </Modal>
          }
        >
          <XlsxLightbox
            fileName={fileName as string}
            url={currentBlobUrl as string}
            onClose={closeModal}
            className="modal-z-middle"
          />
        </Suspense>
      );
    }
    if (fileName && isPdfAttachment(fileName)) {
      return (
        <Suspense
          fallback={
            <Modal isOpen allowClickOutside onClose={closeModal}>
              <ModalHead title="" onClose={closeModal} />
              <ModalBody>
                <FlexBox width="540px" justifyContent="center">
                  <Spinner size={32} />
                </FlexBox>
              </ModalBody>
            </Modal>
          }
        >
          <PDFLightbox
            src={currentBlobUrl as string}
            onClose={closeModal}
            className="modal-z-middle"
          />
        </Suspense>
      );
    }
    if (fileName && isVideoAttachment(fileName)) {
      return (
        <Suspense
          fallback={
            <Modal isOpen allowClickOutside onClose={closeModal}>
              <ModalHead title="" onClose={closeModal} />
              <ModalBody>
                <FlexBox width="540px" justifyContent="center">
                  <Spinner size={32} />
                </FlexBox>
              </ModalBody>
            </Modal>
          }
        >
          <VideoLightbox
            fileName={fileName as string}
            url={currentBlobUrl as string}
            onClose={closeModal}
            className="modal-z-middle"
          />
        </Suspense>
      );
    }
    return null;
  }
}
